import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, X } from '@phosphor-icons/react';
import useAuthStore from '../store/authStore';
import EnvironmentBadge from '../components/EnvironmentBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Dedicated super-admin login. Deliberately at `/vooxy-console` — not linked
 * from anywhere public, not indexed, not discoverable through the marketing
 * "Log In" button. Requires three fields: email, password, admin_code.
 *
 * The admin_code is a shared secret set via `ADMIN_LOGIN_CODE` env var. Even
 * if a super-admin password leaks, an attacker still can't get in without
 * the code. Rotate the code by changing the env var and restarting backend.
 *
 * Every attempt (success + failure) is written to audit_logs, so brute-force
 * attempts are visible in the console under "Admin Console" activity.
 */
export default function AdminConsole() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [adminCode, setAdminCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();

  useEffect(() => {
    document.title = 'Vooxy Console';
    // Reset to the platform (Vooxy) favicon — deliberately overwrite any
    // tenant favicon the browser might still have cached from a prior tenant
    // session. Uses the versioned URL so browsers refetch the current SVG.
    document.querySelectorAll("link[rel*='icon']").forEach(l => l.parentNode.removeChild(l));
    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = '/favicon.svg?v=vooxy-cat-3';
    document.head.appendChild(link);
    const apple = document.createElement('link');
    apple.rel = 'apple-touch-icon';
    apple.href = '/favicon.svg?v=vooxy-cat-3';
    document.head.appendChild(apple);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/admin-login`, {
        email: email.trim(),
        password,
        admin_code: adminCode.trim(),
      });
      const { access_token, user } = res.data;
      if (setAuth) {
        setAuth(access_token, user);
      } else {
        // Older store shape — write directly and set axios header
        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(user));
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      }
      navigate('/super-admin');
    } catch (err) {
      // Server returns generic "Invalid credentials" — surface it as-is.
      setError(err?.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050B18] text-white antialiased" data-testid="admin-console-page">
      <EnvironmentBadge variant="banner" />
      <div className="min-h-[calc(100vh-42px)] flex items-center justify-center p-4">
      {/* Subtle ambient glow — same visual language as the marketing site */}
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-emerald-500/[0.06] blur-[120px]" />
      </div>

      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="mb-8 flex items-center gap-3">
          <img src="/vooxy-logo.svg" alt="Vooxy" className="w-10 h-10 shrink-0" />
          <div className="flex-1">
            <div className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Vooxy</div>
            <div className="text-xs text-slate-500 uppercase tracking-widest">Platform Console</div>
          </div>
          <EnvironmentBadge variant="pill" />
        </div>

        <form
          onSubmit={submit}
          data-testid="admin-console-form"
          className="rounded-2xl bg-white/[0.03] border border-white/10 p-8 backdrop-blur-sm"
        >
          <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Administrator sign-in</h1>
          <p className="text-sm text-slate-400 mb-8">Restricted access. All attempts are logged.</p>

          {error && (
            <div className="mb-5 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm flex items-center justify-between" data-testid="admin-console-error">
              <span>{error}</span>
              <X size={18} className="cursor-pointer opacity-70 hover:opacity-100" onClick={() => setError('')} />
            </div>
          )}

          <Field label="Email">
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              required autoComplete="username"
              data-testid="admin-console-email"
              className={inputClass} placeholder="admin@vooxy.com"
            />
          </Field>

          <Field label="Password" className="mt-4">
            <input
              type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              required autoComplete="current-password"
              data-testid="admin-console-password"
              className={inputClass}
            />
          </Field>

          <Field label="Admin code" className="mt-4">
            <input
              type="password" value={adminCode} onChange={(e) => setAdminCode(e.target.value)}
              required autoComplete="off"
              data-testid="admin-console-code"
              className={`${inputClass} font-mono tracking-wider`}
              placeholder="•••••••••••••••••••"
            />
            <p className="mt-1.5 text-xs text-slate-500">A shared platform secret. Ask the platform owner if you don&apos;t have it.</p>
          </Field>

          <button
            type="submit"
            disabled={loading || !email || !password || !adminCode}
            data-testid="admin-console-submit"
            className="mt-8 w-full px-4 py-3 rounded-lg bg-emerald-500 text-[#050B18] font-bold hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg shadow-emerald-500/20"
          >
            {loading ? 'Signing in…' : 'Enter console'}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-slate-600">
          Not an administrator?{' '}
          <a href="/" className="text-slate-400 hover:text-slate-300">Go home</a>
        </p>
      </div>
      </div>
    </div>
  );
}

const inputClass =
  'w-full px-4 py-2.5 rounded-lg bg-[#050B18] border border-white/10 text-white placeholder-slate-600 text-sm focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 transition-all';

function Field({ label, className = '', children }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">{label}</div>
      {children}
    </label>
  );
}
